"use strict";
/**
 * @fileoverview Nexus错误管理系统 - 智能错误处理和恢复机制
 * @description 提供完整的错误处理功能，包含错误分类、智能恢复、插件沙盒等
 *
 * @author ESEngine Team
 * @version 2.1.0
 * @since 1.0.0
 * @license MIT
 *
 * @example
 * ```typescript
 * const errorManager = NexusErrorManager.getInstance();
 *
 * // 创建插件沙盒
 * const sandbox = errorManager.createPluginSandbox('my-plugin');
 *
 * // 安全执行代码
 * sandbox.execute(() => {
 *   // 可能出错的代码
 * });
 *
 * // 注册恢复策略
 * errorManager.registerRecoveryStrategy('TypeError', new TypeErrorRecoveryStrategy());
 * ```
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ErrorManager = exports.DefaultErrorHandler = exports.NexusErrorManager = exports.PluginSandbox = exports.ErrorCategory = exports.PluginState = exports.ErrorLevel = void 0;
/**
 * 错误级别枚举
 *
 * @description 定义错误的严重程度级别，从低到高依次为：DEBUG < INFO < WARN < ERROR < FATAL
 * @enum {string}
 * @since 1.0.0
 */
var ErrorLevel;
(function (ErrorLevel) {
    /** 调试信息 - 仅在开发环境显示 */
    ErrorLevel["DEBUG"] = "debug";
    /** 一般信息 - 正常运行日志 */
    ErrorLevel["INFO"] = "info";
    /** 警告信息 - 需要注意但不影响运行 */
    ErrorLevel["WARN"] = "warn";
    /** 错误信息 - 影响功能但可恢复 */
    ErrorLevel["ERROR"] = "error";
    /** 致命错误 - 导致系统崩溃 */
    ErrorLevel["FATAL"] = "fatal";
})(ErrorLevel || (exports.ErrorLevel = ErrorLevel = {}));
/**
 * 插件状态枚举
 *
 * @description 定义插件的运行状态
 * @enum {string}
 * @since 2.1.0
 */
var PluginState;
(function (PluginState) {
    /** 未知状态 */
    PluginState["UNKNOWN"] = "unknown";
    /** 正在加载 */
    PluginState["LOADING"] = "loading";
    /** 已加载 */
    PluginState["LOADED"] = "loaded";
    /** 运行中 */
    PluginState["RUNNING"] = "running";
    /** 出现错误 */
    PluginState["ERROR"] = "error";
    /** 已隔离 */
    PluginState["QUARANTINED"] = "quarantined";
    /** 恢复中 */
    PluginState["RECOVERING"] = "recovering";
})(PluginState || (exports.PluginState = PluginState = {}));
/**
 * 错误分类枚举
 *
 * @description 定义错误的分类类型
 * @enum {string}
 * @since 2.1.0
 */
var ErrorCategory;
(function (ErrorCategory) {
    /** 类型错误 */
    ErrorCategory["TYPE_ERROR"] = "TYPE_ERROR";
    /** 引用错误 */
    ErrorCategory["REFERENCE_ERROR"] = "REFERENCE_ERROR";
    /** 依赖错误 */
    ErrorCategory["DEPENDENCY_ERROR"] = "DEPENDENCY_ERROR";
    /** 权限错误 */
    ErrorCategory["PERMISSION_ERROR"] = "PERMISSION_ERROR";
    /** 网络错误 */
    ErrorCategory["NETWORK_ERROR"] = "NETWORK_ERROR";
    /** 语法错误 */
    ErrorCategory["SYNTAX_ERROR"] = "SYNTAX_ERROR";
    /** 运行时错误 */
    ErrorCategory["RUNTIME_ERROR"] = "RUNTIME_ERROR";
    /** 资源错误 */
    ErrorCategory["RESOURCE_ERROR"] = "RESOURCE_ERROR";
    /** 未知错误 */
    ErrorCategory["UNKNOWN_ERROR"] = "UNKNOWN_ERROR";
})(ErrorCategory || (exports.ErrorCategory = ErrorCategory = {}));
/**
 * 插件沙盒
 */
class PluginSandbox {
    constructor(pluginName, errorManager, isolateErrors = true, maxRetries = 3) {
        this.currentRetries = 0;
        this.isQuarantined = false;
        this.pluginName = pluginName;
        this.errorManager = errorManager;
        this.isolateErrors = isolateErrors;
        this.maxRetries = maxRetries;
    }
    /**
     * 在沙盒中执行函数
     */
    execute(fn, context) {
        if (this.isQuarantined) {
            console.warn(`插件 ${this.pluginName} 已被隔离，无法执行操作`);
            return null;
        }
        try {
            return fn();
        }
        catch (error) {
            return this.handleError(error, context);
        }
    }
    /**
     * 在沙盒中执行异步函数
     */
    async executeAsync(fn, context) {
        if (this.isQuarantined) {
            console.warn(`插件 ${this.pluginName} 已被隔离，无法执行操作`);
            return null;
        }
        try {
            return await fn();
        }
        catch (error) {
            return this.handleError(error, context);
        }
    }
    /**
     * 处理错误
     */
    handleError(error, context) {
        const nexusError = {
            code: error.code || 'PLUGIN_ERROR',
            message: error.message || '插件执行错误',
            stack: error.stack,
            source: this.pluginName,
            recoverable: this.currentRetries < this.maxRetries
        };
        const errorContext = {
            pluginName: this.pluginName,
            timestamp: Date.now(),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            url: typeof window !== 'undefined' ? window.location.href : undefined,
            ...context
        };
        // 递增重试次数
        this.currentRetries++;
        // 如果超过最大重试次数，隔离插件
        if (this.currentRetries >= this.maxRetries) {
            this.quarantine();
        }
        // 报告错误
        this.errorManager.reportError(nexusError, errorContext);
        return null;
    }
    /**
     * 隔离插件
     */
    quarantine() {
        this.isQuarantined = true;
        console.error(`插件 ${this.pluginName} 已被隔离，重试次数已达上限`);
        // 触发隔离事件
        this.errorManager.notifyPluginQuarantined(this.pluginName);
    }
    /**
     * 重置沙盒状态
     */
    reset() {
        this.currentRetries = 0;
        this.isQuarantined = false;
    }
    /**
     * 获取沙盒状态
     */
    getStatus() {
        return {
            isQuarantined: this.isQuarantined,
            currentRetries: this.currentRetries,
            maxRetries: this.maxRetries
        };
    }
}
exports.PluginSandbox = PluginSandbox;
/**
 * Nexus错误管理器
 */
class NexusErrorManager {
    constructor() {
        this.errorHandlers = new Map();
        this.errorListeners = [];
        this.errorHistory = [];
        this.sandboxes = new Map();
        this.maxHistorySize = 100;
        this.globalErrorHandlingEnabled = true;
        this.setupGlobalErrorHandling();
    }
    static getInstance() {
        if (!NexusErrorManager.instance) {
            NexusErrorManager.instance = new NexusErrorManager();
        }
        return NexusErrorManager.instance;
    }
    /**
     * 设置全局错误处理
     */
    setupGlobalErrorHandling() {
        if (typeof window === 'undefined')
            return;
        // 捕获未处理的错误
        window.addEventListener('error', (event) => {
            if (this.globalErrorHandlingEnabled) {
                this.handleGlobalError(event.error, {
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno
                });
            }
        });
        // 捕获未处理的Promise拒绝
        window.addEventListener('unhandledrejection', (event) => {
            if (this.globalErrorHandlingEnabled) {
                this.handleGlobalError(event.reason, {
                    type: 'unhandledrejection'
                });
            }
        });
    }
    /**
     * 处理全局错误
     */
    handleGlobalError(error, additionalInfo) {
        const nexusError = {
            code: 'GLOBAL_ERROR',
            message: error.message || '全局错误',
            stack: error.stack,
            source: 'global',
            recoverable: false
        };
        const context = {
            timestamp: Date.now(),
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
            url: typeof window !== 'undefined' ? window.location.href : undefined,
            additionalInfo
        };
        this.reportError(nexusError, context);
    }
    /**
     * 注册错误处理器
     */
    registerErrorHandler(name, handler) {
        this.errorHandlers.set(name, handler);
        console.log(`错误处理器 "${name}" 已注册`);
    }
    /**
     * 注销错误处理器
     */
    unregisterErrorHandler(name) {
        this.errorHandlers.delete(name);
        console.log(`错误处理器 "${name}" 已注销`);
    }
    /**
     * 添加错误监听器
     */
    addErrorListener(listener) {
        this.errorListeners.push(listener);
    }
    /**
     * 移除错误监听器
     */
    removeErrorListener(listener) {
        const index = this.errorListeners.indexOf(listener);
        if (index > -1) {
            this.errorListeners.splice(index, 1);
        }
    }
    /**
     * 创建插件沙盒
     */
    createPluginSandbox(pluginName, isolateErrors = true, maxRetries = 3) {
        const sandbox = new PluginSandbox(pluginName, this, isolateErrors, maxRetries);
        this.sandboxes.set(pluginName, sandbox);
        return sandbox;
    }
    /**
     * 获取插件沙盒
     */
    getPluginSandbox(pluginName) {
        return this.sandboxes.get(pluginName);
    }
    /**
     * 报告错误
     */
    reportError(error, context) {
        // 添加到历史记录
        this.addToHistory(error, context);
        // 调用错误处理器
        this.invokeErrorHandlers(error, context);
        // 通知监听器
        this.notifyListeners(error, context);
        // 记录错误日志
        this.logError(error, context);
    }
    /**
     * 添加到历史记录
     */
    addToHistory(error, context) {
        this.errorHistory.push({ error, context });
        // 保持历史记录大小
        if (this.errorHistory.length > this.maxHistorySize) {
            this.errorHistory.shift();
        }
    }
    /**
     * 调用错误处理器
     */
    invokeErrorHandlers(error, context) {
        for (const [name, handler] of this.errorHandlers) {
            try {
                handler.handle(error);
                // 如果处理器可以恢复错误，尝试恢复
                if (error.recoverable && handler.canRecover(error)) {
                    handler.recover(error).catch(recoveryError => {
                        console.error(`错误恢复失败 (${name}):`, recoveryError);
                    });
                }
            }
            catch (handlerError) {
                console.error(`错误处理器 "${name}" 执行失败:`, handlerError);
            }
        }
    }
    /**
     * 通知监听器
     */
    notifyListeners(error, context) {
        for (const listener of this.errorListeners) {
            try {
                listener(error, context);
            }
            catch (listenerError) {
                console.error('错误监听器执行失败:', listenerError);
            }
        }
    }
    /**
     * 记录错误日志
     */
    logError(error, context) {
        const logLevel = this.getLogLevel(error);
        const logMessage = this.formatLogMessage(error, context);
        switch (logLevel) {
            case ErrorLevel.FATAL:
                console.error('🔥 [FATAL]', logMessage);
                break;
            case ErrorLevel.ERROR:
                console.error('❌ [ERROR]', logMessage);
                break;
            case ErrorLevel.WARN:
                console.warn('⚠️ [WARN]', logMessage);
                break;
            case ErrorLevel.INFO:
                console.info('ℹ️ [INFO]', logMessage);
                break;
            case ErrorLevel.DEBUG:
                console.debug('🐛 [DEBUG]', logMessage);
                break;
        }
    }
    /**
     * 获取日志级别
     */
    getLogLevel(error) {
        if (error.code === 'GLOBAL_ERROR')
            return ErrorLevel.FATAL;
        if (error.code.includes('PLUGIN_ERROR'))
            return ErrorLevel.ERROR;
        if (error.code.includes('WARN'))
            return ErrorLevel.WARN;
        if (error.code.includes('INFO'))
            return ErrorLevel.INFO;
        if (error.code.includes('DEBUG'))
            return ErrorLevel.DEBUG;
        return ErrorLevel.ERROR;
    }
    /**
     * 格式化日志消息
     */
    formatLogMessage(error, context) {
        const parts = [];
        if (context.pluginName) {
            parts.push(`[${context.pluginName}]`);
        }
        if (context.panelName) {
            parts.push(`[${context.panelName}]`);
        }
        if (context.componentName) {
            parts.push(`[${context.componentName}]`);
        }
        parts.push(error.message);
        if (error.stack) {
            parts.push(`\n${error.stack}`);
        }
        return parts.join(' ');
    }
    /**
     * 通知插件被隔离
     */
    notifyPluginQuarantined(pluginName) {
        const context = {
            pluginName,
            timestamp: Date.now()
        };
        const error = {
            code: 'PLUGIN_QUARANTINED',
            message: `插件 ${pluginName} 已被隔离`,
            source: pluginName,
            recoverable: false
        };
        this.reportError(error, context);
    }
    /**
     * 获取错误历史
     */
    getErrorHistory() {
        return [...this.errorHistory];
    }
    /**
     * 清空错误历史
     */
    clearErrorHistory() {
        this.errorHistory = [];
    }
    /**
     * 获取错误统计
     */
    getErrorStats() {
        const stats = {
            totalErrors: this.errorHistory.length,
            errorsBySource: new Map(),
            errorsByCode: new Map(),
            recentErrors: 0
        };
        const oneHourAgo = Date.now() - 60 * 60 * 1000;
        for (const { error, context } of this.errorHistory) {
            // 按来源统计
            const source = error.source || 'unknown';
            stats.errorsBySource.set(source, (stats.errorsBySource.get(source) || 0) + 1);
            // 按错误代码统计
            stats.errorsByCode.set(error.code, (stats.errorsByCode.get(error.code) || 0) + 1);
            // 最近一小时的错误
            if (context.timestamp > oneHourAgo) {
                stats.recentErrors++;
            }
        }
        return stats;
    }
    /**
     * 启用/禁用全局错误处理
     */
    setGlobalErrorHandling(enabled) {
        this.globalErrorHandlingEnabled = enabled;
    }
    /**
     * 获取所有沙盒状态
     */
    getSandboxStatuses() {
        const statuses = new Map();
        for (const [name, sandbox] of this.sandboxes) {
            statuses.set(name, sandbox.getStatus());
        }
        return statuses;
    }
    /**
     * 重置所有沙盒
     */
    resetAllSandboxes() {
        for (const sandbox of this.sandboxes.values()) {
            sandbox.reset();
        }
        console.log('所有插件沙盒已重置');
    }
    /**
     * 销毁错误管理器
     */
    destroy() {
        this.errorHandlers.clear();
        this.errorListeners = [];
        this.errorHistory = [];
        this.sandboxes.clear();
        this.globalErrorHandlingEnabled = false;
    }
}
exports.NexusErrorManager = NexusErrorManager;
/**
 * 默认错误处理器
 */
class DefaultErrorHandler {
    handle(error) {
        // 默认处理逻辑
        console.error('默认错误处理器:', error);
    }
    canRecover(error) {
        return error.recoverable || false;
    }
    async recover(error) {
        // 简单的恢复逻辑
        console.log('尝试恢复错误:', error.code);
        return true;
    }
}
exports.DefaultErrorHandler = DefaultErrorHandler;
// 导出单例实例
exports.ErrorManager = NexusErrorManager.getInstance();
